import type { one_to_one } from '@fireworkhq/proto-contract'
import type {
  Message,
  MessageAttachment,
} from '@oneToOne/shared/components/chat/types'
import { sortMessages } from '@oneToOne/shared/utils/sortMessages'
import { transformMessage } from '@oneToOne/shared/utils/transformMessage'
import { uniqBy } from 'es-toolkit'

import { uuidv4 } from '@core/helpers/uuidv4'
import type { GetState, NamedSet } from '@core/zustand'

export interface OneToOneSharedState {
  clientControls: ClientControls
  messages: Message[]
  nextMessagesCursor: string | null
  isTyping: boolean
  fullScreenPreview: { attachment: MessageAttachment } | undefined
}

type MessageType = 'agent' | 'visitor'

interface ClientControls {
  cameraEnabled: boolean
  microphoneMuted: boolean
}

function addChatMessages(
  set: NamedSet<OneToOneSharedState>,
  messages: one_to_one.shared.Message[],
  type: MessageType,
  nextMessagesCursor?: string | null,
) {
  set(
    (prevState) => {
      // Needs uniqBy here because we accept incoming agent messages on agent's side for session switching
      // and media messages will add duplicate message since it involves upload
      // Id of the message will be updated in `replaceMessage` after `sendMessage` ack instead
      const newMessages = sortMessages(
        uniqBy(
          [
            ...prevState.messages,
            ...messages.map((m) => transformMessage(m, type)),
          ],
          (m) => {
            if (m.client_msg_ref !== undefined) {
              return m.client_msg_ref
            } else {
              return m.id
            }
          },
        ),
      )

      return {
        messages: newMessages,
        nextMessagesCursor:
          typeof nextMessagesCursor !== 'undefined'
            ? nextMessagesCursor
            : prevState.nextMessagesCursor,
      }
    },
    false,
    'addChatMessages',
  )
}

/**
 *  Shared actions between chat channel and live helper.
 */
export const sharedOneToOneActions = (
  set: NamedSet<OneToOneSharedState>,
  _get: GetState<OneToOneSharedState>,
) => ({
  cameraEnabledStateToggled: () => {
    set(
      (prevState) => {
        return {
          ...prevState,
          clientControls: {
            ...prevState.clientControls,
            cameraEnabled: !prevState.clientControls.cameraEnabled,
          },
        }
      },
      false,
      'cameraEnabledStateToggled',
    )
  },
  microphoneMutedStateToggled: () => {
    set(
      (prevState) => {
        return {
          ...prevState,
          clientControls: {
            ...prevState.clientControls,
            microphoneMuted: !prevState.clientControls.microphoneMuted,
          },
        }
      },
      false,
      'microphoneMutedStateToggled',
    )
  },
  setClientControls: (clientControls: ClientControls) => {
    set({ clientControls }, false, 'setClientControls')
  },
  setChatMessages: (
    messages: one_to_one.shared.Message[],
    type: MessageType,
    nextMessagesCursor?: string | null,
  ) => {
    set(
      (prevState) => ({
        messages: sortMessages(messages.map((m) => transformMessage(m, type))),
        nextMessagesCursor:
          typeof nextMessagesCursor !== 'undefined'
            ? nextMessagesCursor
            : prevState.nextMessagesCursor,
      }),
      false,
      'setChatMessages',
    )
  },
  addChatMessages: (
    messages: one_to_one.shared.Message[],
    type: MessageType,
    nextMessagesCursor?: string | null,
  ) => {
    addChatMessages(set, messages, type, nextMessagesCursor)
  },
  updateOtherPartyTypingStatus: (isTyping: boolean) => {
    set(
      () => ({
        isTyping,
      }),
      false,
      'updateOtherPartyTypingStatus',
    )
  },
  replaceMessage(message: one_to_one.shared.Message, type: MessageType) {
    set(
      (prevState) => ({
        messages: prevState.messages.map((m) => {
          if (m.client_msg_ref === message.client_msg_ref) {
            return transformMessage(message, type)
          }
          return m
        }),
      }),
      false,
      'replaceMessage',
    )
  },
  updateMessage(message: Message) {
    set(
      (prevState) => ({
        messages: prevState.messages.map((m) => {
          if (m.client_msg_ref === message.client_msg_ref) {
            return message
          }
          return m
        }),
      }),
      false,
      'updateMessage',
    )
  },
  createMediaMessage: (
    attachment: MessageAttachment,
    type: MessageType,
  ): one_to_one.shared.Message => {
    const author = type === 'agent' ? 'AUTHOR_AGENT' : 'AUTHOR_VISITOR'

    const message: one_to_one.shared.Message = {
      client_msg_ref: uuidv4(),
      created_at: new Date().toISOString(),
      conversation_id: undefined,
      media_message: {
        attachments: [attachment],
        author,
      },
    }
    addChatMessages(set, [message], type)
    return message
  },
  showFullScreenPreview: (attachment: MessageAttachment) => {
    set({ fullScreenPreview: { attachment } }, false, 'showFullScreenPreview')
  },
  hideFullScreenPreview: () => {
    set({ fullScreenPreview: undefined }, false, 'hideFullScreenPreview')
  },
})
